import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";

const GOLD = "#FFEE20";

// Lightweight pre-upload step for gallery images: preview the selected files and
// optionally attach a description (applied to the batch) before uploading.
export default function GalleryUploadDialog({ open, files = [], uploading = false, onCancel, onConfirm }) {
  const [caption, setCaption] = useState("");

  const previews = useMemo(() => files.map((f) => ({ url: URL.createObjectURL(f), name: f.name })), [files]);

  // Revoke object URLs when the file set changes or the dialog unmounts.
  useEffect(() => {
    return () => previews.forEach((p) => URL.revokeObjectURL(p.url));
  }, [previews]);

  // Reset the caption each time a fresh batch is opened.
  useEffect(() => {
    if (open) setCaption("");
  }, [open, files]);

  return (
    <Dialog
      open={open}
      onClose={uploading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { bgcolor: "#12121A", color: "#fff", borderRadius: "14px" } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {files.length > 1 ? `Add ${files.length} images` : "Add image"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1, mb: 1.5 }}>
          {previews.map((p) => (
            <Box
              key={p.url}
              component="img"
              src={p.url}
              alt={p.name}
              sx={{ width: 84, height: 84, objectFit: "cover", borderRadius: "10px", flexShrink: 0 }}
            />
          ))}
        </Box>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          size="small"
          placeholder="Add a description… (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          inputProps={{ maxLength: 500 }}
          sx={{
            "& .MuiOutlinedInput-root": { bgcolor: "#1a1a1a", color: "#fff", borderRadius: "10px" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2a2a2a" },
          }}
        />
        {files.length > 1 && (
          <Box sx={{ color: "#888", fontSize: 12, mt: 0.5 }}>
            The description is applied to all {files.length} images. You can edit each one later.
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={uploading} sx={{ color: "#aaa", textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(caption.trim())}
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={16} sx={{ color: "#000" }} /> : null}
          sx={{
            background: GOLD,
            color: "#000",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { background: "#FFE066" },
          }}
        >
          {uploading ? "Uploading…" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
